import React, { useEffect, useMemo, useState } from "react";
import { TbPlane, TbReceipt } from "react-icons/tb";
import { Approval, Button, ButtonGroup, CardsRenderer, CardsWrapper, InlineFilterSelect, Searchbox, Toast } from "../../../../Components";
import { isOutOfPolicy } from "../../../../Components/OutOfPolicyBadge/status";
import { apis, get } from "../../../../Utils/axios";

const SORTS = [
	{ label: "As arrived", value: "arrived" },
	{ label: "Soonest date", value: "date-asc" },
	{ label: "Latest date", value: "date-desc" },
	{ label: "Highest amount", value: "amount-desc" },
	{ label: "Lowest amount", value: "amount-asc" },
	{ label: "Requestor name", value: "name" },
];

const POLICIES = [
	{ label: "All requests", value: "all" },
	{ label: "In policy", value: "inPolicy" },
	{ label: "Out of policy", value: "violations" },
];

const requestorName = (approval) => `${approval.requestor?.fName || ""} ${approval.requestor?.lName || ""}`.trim();

const violationText = (approval) => (Array.isArray(approval.violations) ? approval.violations : []);

const flightTokens = (approval) => {
	const flight = approval.request?.data?.flight;
	if (!flight) return [];
	const segments = [...(flight.segments || []), ...(flight.onwardSegments || []), ...(flight.returnSegments || [])];
	return segments.flatMap((segment) => [segment?.departureCode, segment?.arrivalCode, segment?.airlineName, segment?.airlineCode, segment?.flightNumber]);
};

const searchText = (approval) =>
	[
		`APP-${approval.id}`,
		approval.id,
		approval.amount,
		approval.type,
		approval.remarks,
		requestorName(approval),
		approval.requestor?.email,
		...violationText(approval),
		...flightTokens(approval),
	]
		.filter(Boolean)
		.join(" ")
		.toLowerCase();

const sortApprovals = (items, sort, order) => {
	const sorted = [...items];
	if (sort === "date-asc") return sorted.sort((a, b) => new Date(a.date) - new Date(b.date));
	if (sort === "date-desc") return sorted.sort((a, b) => new Date(b.date) - new Date(a.date));
	if (sort === "amount-desc") return sorted.sort((a, b) => Number(b.amount) - Number(a.amount));
	if (sort === "amount-asc") return sorted.sort((a, b) => Number(a.amount) - Number(b.amount));
	if (sort === "name") return sorted.sort((a, b) => requestorName(a).localeCompare(requestorName(b)));
	return sorted.sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
};

function ApprovalGroup({ title, icon, items, loading, emptyLabel, onReload }) {
	return (
		<div className="mt-4">
			<div className="flex items-center gap-2 text-sm font-semibold text-dark">
				{icon}
				{title}
				{!loading ? <span className="font-medium text-secondary">{items.length}</span> : null}
			</div>
			<CardsWrapper loading={loading} className="mt-3">
				<CardsRenderer items={items} emptyLabel={emptyLabel} render={(approval) => <Approval key={approval.id} {...approval} onReload={onReload} />} />
			</CardsWrapper>
		</div>
	);
}

function ApprovalTasks() {
	const [ApprovalsToday, setApprovalsToday] = useState([]);
	const [Loading, setLoading] = useState(true);
	const [Search, setSearch] = useState("");
	const [SearchKey, setSearchKey] = useState(0);
	const [Type, setType] = useState({ label: "All", value: "all" });
	const [Sort, setSort] = useState(SORTS[0].value);
	const [Policy, setPolicy] = useState(POLICIES[0].value);

	useEffect(() => {
		fetchApprovals();
	}, []);

	const fetchApprovals = () => {
		setLoading(true);
		get(apis.getTodaysApprovals, {}, (r, e) => {
			if (r) {
				setApprovalsToday(r.approvals || []);
				setLoading(false);
			} else if (e) {
				Toast.handleError(e);
				setLoading(false);
			}
		});
	};

	const order = useMemo(() => new Map(ApprovalsToday.map((approval, index) => [approval.id, index])), [ApprovalsToday]);
	const query = Search.trim().toLowerCase();
	const filtersActive = Boolean(query) || Type.value !== "all" || Sort !== "arrived" || Policy !== "all";

	const matched = useMemo(() => {
		const narrowed = ApprovalsToday.filter((approval) => {
			if (Policy === "violations" && !isOutOfPolicy(approval.violations)) return false;
			if (Policy === "inPolicy" && isOutOfPolicy(approval.violations)) return false;
			if (query && !searchText(approval).includes(query)) return false;
			return true;
		});
		return sortApprovals(narrowed, Sort, order);
	}, [ApprovalsToday, Policy, query, Sort, order]);

	const flights = matched.filter((approval) => approval.type === "flight");
	const expenses = matched.filter((approval) => approval.type === "expense");
	const visibleCount = (Type.value === "expense" ? 0 : flights.length) + (Type.value === "flight" ? 0 : expenses.length);
	const emptyLabel = (kind) => (filtersActive ? `No ${kind} approvals match these filters` : `No ${kind} approvals arrived today`);

	const clearFilters = () => {
		setSearch("");
		setSearchKey((key) => key + 1);
		setType({ label: "All", value: "all" });
		setSort(SORTS[0].value);
		setPolicy(POLICIES[0].value);
	};

	return (
		<div>
			<h3 className="mt-8">Today's Approvals</h3>
			<div className="mt-4 flex flex-col gap-3">
				<div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
					<ButtonGroup
						onClick={setType}
						size="xs !p-1.5"
						selected={Type}
						className="text-sm"
						options={[
							{ label: "All", value: "all" },
							{ label: "Flights", value: "flight" },
							{ label: "Expenses", value: "expense" },
						]}
					/>
					<div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap lg:w-auto lg:items-center lg:justify-end">
						<Searchbox key={SearchKey} className="w-full sm:w-72" placeholder="Search name, email, id, amount, route" cmd="f" onChange={setSearch} />
						<InlineFilterSelect className="w-full sm:w-44" prefix="Sort" options={SORTS} value={Sort} onChange={(option) => setSort(option.value)} />
						<InlineFilterSelect className="w-full sm:w-44" prefix="Policy" options={POLICIES} value={Policy} onChange={(option) => setPolicy(option.value)} />
					</div>
				</div>
				<div className="flex items-center justify-between gap-3 text-xs text-secondary">
					<div>
						Showing {visibleCount} of {ApprovalsToday.length}
					</div>
					{filtersActive ? (
						<Button onClick={clearFilters} variant="outlined" className="btn-xs">
							Clear filters
						</Button>
					) : null}
				</div>
			</div>
			{Type.value !== "expense" ? (
				<ApprovalGroup title="Flights" icon={<TbPlane className="text-xl" />} items={flights} loading={Loading} emptyLabel={emptyLabel("flight")} onReload={fetchApprovals} />
			) : null}
			{Type.value !== "flight" ? (
				<ApprovalGroup title="Expenses" icon={<TbReceipt className="text-xl" />} items={expenses} loading={Loading} emptyLabel={emptyLabel("expense")} onReload={fetchApprovals} />
			) : null}
		</div>
	);
}

export default ApprovalTasks;
